import axios from 'axios';
import { toast } from '../hooks/use-toast';

// Get backend URL from environment variable or window global (for webpack builds) or production fallback
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.REACT_APP_BACKEND_URL || 'https://api.vivyawatch.com';

// CSRF token cache
let csrfToken = null;

// ── Impersonation timeout recovery flag ──────────────────────────────────────
// Prevents multiple concurrent 401 handlers from all firing the restore flow.
let _impersonationRecoveryInProgress = false;

// ── Silent token refresh state ────────────────────────────────────────────────
// A single in-flight refresh promise is shared across all concurrent 401s so
// we only call the backend once, then retry all queued requests together.
let _tokenRefreshPromise = null;

// ── Persistent-401 (unrecoverable session) guard ──────────────────────────────
// If /api/auth/token/refresh reports success but the very next request still
// gets a 401 (e.g. session was revoked server-side, replay-attack detection,
// account disabled mid-session), retrying refresh forever would spin an
// infinite loop. Once we detect this, clear auth state, show a friendly
// message, and hard-redirect to sign-in exactly once.
let _sessionExpiredHandled = false;
function _handleUnrecoverableAuthFailure(currentPath) {
  if (_sessionExpiredHandled) return;
  _sessionExpiredHandled = true;

  localStorage.removeItem('authToken');
  localStorage.removeItem('currentUser');

  const targetUrl = currentPath && currentPath.startsWith('/super-admin') ? '/super-admin/' : '/signin';

  toast({
    title: 'Session expired',
    description: 'Your session has ended. Redirecting you to sign in…',
    variant: 'destructive',
  });

  window.dispatchEvent(new CustomEvent('vivya:session-expired', {
    detail: { message: 'Your session has ended. Please sign in again.' }
  }));

  // Give the toast a moment to render before the hard navigation clears it.
  setTimeout(() => {
    window.location.href = targetUrl;
  }, 1200);
}

// ── Account suspension guard ──────────────────────────────────────────────────
// When a tenant's payment has lapsed, ALL non-billing/non-auth API requests are
// cancelled at the request interceptor level — before they reach the network.
// The flag is persisted in sessionStorage so that page reloads after the first
// 402 detection also benefit from instant blocking (no initial burst of 402s).
const _SUSPENSION_SESSION_KEY = 'vivya_account_suspended';
let _accountSuspended = sessionStorage.getItem(_SUSPENSION_SESSION_KEY) === 'true';

/** URL patterns that are always allowed, even when the account is suspended. */
const _SUSPENSION_ALLOWLIST = [
  /\/api\/auth\//,
  /\/api\/billing\//,
  /\/api\/profile\/settings/,
];
const _isSuspensionAllowed = (url = '') =>
  _SUSPENSION_ALLOWLIST.some((pattern) => pattern.test(url));

/**
 * Mark the account as suspended or restored.
 * Call with `true` when a PAYMENT_REQUIRED 402 is detected.
 * Call with `false` after a successful payment verification.
 * Exported so AdminDashboard can proactively set it on lockout detection.
 */
export const markAccountSuspended = (suspended) => {
  _accountSuspended = suspended;
  if (suspended) {
    sessionStorage.setItem(_SUSPENSION_SESSION_KEY, 'true');
  } else {
    sessionStorage.removeItem(_SUSPENSION_SESSION_KEY);
  }
};

// ============================================================
// Centralized Response Cache & Request Deduplication
// ============================================================

// Cache for GET responses: Map<cacheKey, { data, timestamp }>
const responseCache = new Map();

// In-flight request tracker for deduplication: Map<cacheKey, Promise>
const pendingRequests = new Map();

// Default TTL per URL pattern (ms). More specific patterns checked first.
const CACHE_TTL_RULES = [
  { pattern: /\/api\/org-modules\//, ttl: 120000 },      // 2 min - modules rarely change
  { pattern: /\/api\/tenant-settings\//, ttl: 120000 },   // 2 min - settings rarely change
  { pattern: /\/api\/dashboard\/stats/, ttl: 30000 },     // 30 sec - dashboard refreshes often
  { pattern: /\/api\/search\//, ttl: 0 },                 // no cache - search is user-driven
  { pattern: /\/api\/auth\//, ttl: 0 },                   // no cache - auth is sensitive
  { pattern: /\/api\/voip\//, ttl: 0 },                   // no cache - VOIP is real-time call data; filter changes must always hit DB fresh
  { pattern: /\/api\/admin-notifications\//, ttl: 0 },   // no cache - real-time notification polling
  { pattern: /\/api\/profile\//, ttl: 0 },                 // no cache — profile is per-user; always fetch live
  { pattern: /\/api\/geolocation\//, ttl: 0 },              // no cache - device/time filters must always hit DB fresh
  { pattern: /\/api\/dlp\/domain-blocking\//, ttl: 0 },    // no cache - domain blocking filters must always hit DB fresh
  { pattern: /\/api\/security\/usb-events/, ttl: 0 },     // no cache - real-time USB event polling (15s interval)
  { pattern: /\/api\/security\/session-events/, ttl: 0 }, // no cache - real-time session events
  { pattern: /\/api\/security\//, ttl: 10000 },           // 10 sec for other security endpoints
  { pattern: /\/api\/recording\/list/, ttl: 10000 },      // 10 sec - recording status changes frequently
  { pattern: /\/api\/calendar\/(calendar-employees|device-enrolled-teams|synced-employees|synced-teams)/, ttl: 120000 }, // 2 min - directory data, rarely changes within a session
  { pattern: /\/api\/calendar\/top-meeting-users/, ttl: 60000 }, // 1 min - aggregated chip strip
  // Exfiltration Detection — heavy analysis pipeline; TTL matches the 10-min backend cache
  { pattern: /\/api\/exfiltration-detection\/analyze/, ttl: 600000 }, // 10 min — matches backend TTL
  { pattern: /\/api\/exfiltration-detection\//, ttl: 120000 },        // 2 min for other exfil endpoints
  // UEBA — anomaly detection & baselines are expensive aggregations; fine to cache 2 min
  { pattern: /\/api\/ueba\/stats/, ttl: 120000 },          // 2 min
  { pattern: /\/api\/ueba\/anomalies/, ttl: 120000 },      // 2 min
  { pattern: /\/api\/ueba\/baselines/, ttl: 120000 },      // 2 min
  { pattern: /\/api\/ueba\/trend/, ttl: 120000 },          // 2 min
  { pattern: /\/api\/ueba\//, ttl: 60000 },                // 1 min for other ueba endpoints
  // SOAR — playbooks/executions/decisions change infrequently; attack-chains scan is expensive
  { pattern: /\/api\/soar\/attack-chains/, ttl: 180000 },  // 3 min — scan is costly
  { pattern: /\/api\/soar\/effectiveness/, ttl: 180000 },  // 3 min — feedback aggregation is slow
  { pattern: /\/api\/soar\/tuning-recommendations/, ttl: 180000 }, // 3 min
  { pattern: /\/api\/soar\/ai-decisions\/volume/, ttl: 120000 },   // 2 min
  { pattern: /\/api\/soar\/ai-decisions\/counts/, ttl: 120000 },   // 2 min
  { pattern: /\/api\/soar\/ai-decisions\/threat-levels/, ttl: 120000 }, // 2 min
  { pattern: /\/api\/soar\/stats/, ttl: 60000 },           // 1 min — counts change more often
  { pattern: /\/api\/soar\//, ttl: 60000 },                // 1 min for all other soar endpoints
  // App Monitoring — 2-minute TTL so tab switches and minor filter re-renders return cached data.
  // Analytical dashboard data is stable within a session; the Refresh button bypasses the cache.
  // B4: dashboard-init bundles 8 sub-queries; give it the full 5-min backend cache TTL so a
  // second visit within that window returns instantly from the browser cache.
  { pattern: /\/api\/app-monitoring\/dashboard-init/, ttl: 300000 }, // 5 min — matches backend OVERVIEW_CACHE_TTL
  { pattern: /\/api\/app-monitoring\//, ttl: 120000 },     // 2 min — team overview, zenai-insights, score-details, etc.
  { pattern: /\/api\/ca\/apps\//, ttl: 120000 },            // 2 min — apps overview, sessions, list
  { pattern: /\/api\/productivity\//, ttl: 120000 },        // 2 min — productivity overview (SSOT score)
  { pattern: /\/api\//, ttl: 30000 },                      // 30 sec default for all other API
];

/**
 * Get the cache TTL for a given URL.
 * Returns 0 to skip caching for that URL.
 */
const getCacheTTL = (url) => {
  for (const rule of CACHE_TTL_RULES) {
    if (rule.pattern.test(url)) return rule.ttl;
  }
  return 30000; // fallback 30s
};

/**
 * Build a cache key from method + url + sorted params.
 */
const buildCacheKey = (config) => {
  const params = config.params ? JSON.stringify(config.params, Object.keys(config.params).sort()) : '';
  return `${config.method}:${config.url}:${params}`;
};

/**
 * Clear cached responses. Optionally pass a URL pattern to clear only matching entries.
 * Call this after mutations (POST/PUT/DELETE) that invalidate cached data.
 */
export const clearApiCache = (urlPattern = null) => {
  if (!urlPattern) {
    responseCache.clear();
    return;
  }
  // Normalize away a trailing slash so a mutated item URL like
  // "/api/shifts/templates/{id}" (which reduces to base path
  // "/api/shifts/templates/") still matches a collection GET cached as
  // "/api/shifts/templates" (no trailing slash — see request interceptor,
  // which strips trailing slashes to avoid FastAPI 307 redirects).
  const trimmedPattern = urlPattern.endsWith('/') ? urlPattern.slice(0, -1) : urlPattern;
  for (const key of responseCache.keys()) {
    // key format: "method:url:params" — url itself never contains ':'
    const keyUrl = key.split(':')[1] || '';
    if (keyUrl === trimmedPattern || keyUrl.startsWith(`${trimmedPattern}/`)) {
      responseCache.delete(key);
    }
  }
};

// Create axios instance with default config
const api = axios.create({
  baseURL: BACKEND_URL,
  // 120 seconds - "All Teams/All Devices" analytics (e.g. /focus-trends,
  // /collaboration-ratio over a 28-day range on orgs with 100+ devices)
  // measures 75-95s server-side; 90s cut these off right at the finish line.
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Enable cookies for CSRF token
});

// Fetch CSRF token from backend
const fetchCSRFToken = async () => {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/auth/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Resolve which UI the current request is being made from, purely from the
// browser URL — used ONLY to tag Audit Activity log entries as "Admin" vs
// "Employee" portal context (backend/services/admin_activity_service.py).
// Never used for authorization; RBAC remains solely JWT-role-based.
const _resolvePortalContext = () => {
  try {
    const path = window.location.pathname || '';
    if (path.startsWith('/dashboard')) return 'admin';
    if (path === '/employee-portal' || path.startsWith('/employee-portal/') || path.startsWith('/my-')) {
      return 'employee';
    }
  } catch (e) {
    // window unavailable (SSR/tests) — omit the header
  }
  return null;
};

// True while the browser is on an Employee Portal page (same URL rule as above).
export const isEmployeePortalRoute = () => _resolvePortalContext() === 'employee';

// Request interceptor to add CSRF token and normalize URLs
api.interceptors.request.use(
  async (config) => {
    // ── Suspension guard ──────────────────────────────────────────────────────
    // If the account is suspended (PAYMENT_REQUIRED), cancel all non-billing /
    // non-auth requests before they hit the network. This prevents the flood of
    // 402 errors from background contexts (SettingsContext, ScoringConfigContext,
    // etc.) that fire on every page load while the account is locked.
    if (_accountSuspended && !_isSuspensionAllowed(config.url || '')) {
      // Using a named cancellation so callers can distinguish from real errors.
      return Promise.reject(
        Object.assign(new Error('account_suspended'), { _isSuspensionCancel: true })
      );
    }

    // SECURITY: httpOnly cookie sent automatically by browser
    // No need to add Authorization header - JWT in httpOnly cookie is used

    // Add CSRF token for state-changing operations (POST, PUT, DELETE, PATCH)
    const stateMutatingMethods = ['post', 'put', 'delete', 'patch'];
    if (stateMutatingMethods.includes(config.method?.toLowerCase())) {
      // Fetch CSRF token if not cached
      if (!csrfToken) {
        await fetchCSRFToken();
      }

      // Add CSRF token to headers
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Tag the request with which portal UI it originated from (Audit Activity
    // classification only — see _resolvePortalContext above).
    const portalContext = _resolvePortalContext();
    if (portalContext) {
      config.headers['X-Portal-Context'] = portalContext;
    }

    // Strip trailing slashes from API URLs to prevent 307 redirects
    // FastAPI routes are defined WITHOUT trailing slashes, adding them causes 307 redirect overhead
    if (config.url && config.url.startsWith('/api/')) {
      const [path, queryString] = config.url.split('?');
      if (path.length > 1 && path.endsWith('/')) {
        const trimmed = path.replace(/\/+$/, '');
        config.url = queryString ? `${trimmed}?${queryString}` : trimmed;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Several HRMS components call the bare `axios` singleton directly (with a
// local BACKEND_URL constant) instead of this `api` instance — register the
// same portal-context tagging there too, so those requests aren't silently
// missed by Audit Activity classification. See _resolvePortalContext above.
axios.interceptors.request.use(
  (config) => {
    const portalContext = _resolvePortalContext();
    if (portalContext && !config.headers['X-Portal-Context']) {
      config.headers['X-Portal-Context'] = portalContext;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Human-readable display names for internal module IDs used in backend 403 messages
const MODULE_DISPLAY_NAMES = {
  devicesAgents: 'Devices & Agents',
  alerts: 'Alerts',
  productivity: 'Productivity',
  attendanceHr: 'Attendance & HR',
  cloudDocuments: 'Cloud Documents',
  liveMonitoring: 'Live Monitoring',
  reports: 'Reports',
  settings: 'Settings',
  biometricDevices: 'Biometric Devices',
  users: 'Users',
};

// Handle 403 MODULE_DISABLED — module not in license features_enabled
// Handle 403 RBAC ACCESS_DENIED — user lacks role permission for this resource
// Shared by the axios response interceptor and errorFromFetchResponse() so raw
// fetch() callers raise the same global toast.
const notifyForbidden = (data, config) => {
  const code = data?.code;
  const detail = data?.detail;

  if (code === 'MODULE_DISABLED') {
    const moduleName = data?.module || 'unknown';
    const moduleDisplay = MODULE_DISPLAY_NAMES[moduleName] || moduleName;
    window.dispatchEvent(new CustomEvent('vivya:module-disabled', {
      detail: {
        module: moduleName,
        message: data?.detail
          ? data.detail.replace(moduleName, moduleDisplay)
          : `Module '${moduleDisplay}' is not included in your license.`,
      }
    }));
    console.warn(`[LICENSE] Module disabled: ${moduleName}`);
  } else if (
    code === 'ACCESS_DENIED' ||
    (typeof detail === 'string' && !detail.toLowerCase().includes('csrf') && detail !== 'Not authenticated')
  ) {
    // Skip toast for background calls that explicitly suppress access-denied notifications
    if (config?._suppressAccessDenied) {
      console.warn('[RBAC] Access denied (suppressed):', detail);
    } else {
      // RBAC permission denied — user is authenticated but lacks required role permission
      // Replace raw module IDs in the backend message with human-readable names
      let message = data?.detail || 'You do not have permission to perform this action.';
      Object.entries(MODULE_DISPLAY_NAMES).forEach(([id, name]) => {
        message = message.replace(new RegExp(`'${id}'`, 'g'), `'${name}'`);
      });
      const module = data?.module || null;
      window.dispatchEvent(new CustomEvent('vivya:access-denied', {
        detail: { message, module, code: code || 'ACCESS_DENIED' }
      }));
      console.warn('[RBAC] Access denied:', message);
    }
  }
};

/**
 * Build an axios-shaped Error from a failed raw fetch() Response so callers can
 * use parseApiError() / isGlobalAccessDeniedError() exactly as they do for axios,
 * and so a 403 still raises the global RBAC / module-disabled toast.
 */
export const errorFromFetchResponse = async (response) => {
  let data = null;
  try {
    data = await response.clone().json();
  } catch {
    // Non-JSON body (blob, HTML error page, empty) — leave data null
  }

  if (response.status === 403) {
    notifyForbidden(data, null);
  }

  const error = new Error(
    (typeof data?.detail === 'string' && data.detail) || response.statusText || 'Request failed'
  );
  error.response = { status: response.status, data };
  return error;
};

// Response interceptor for error handling and CSRF token updates
api.interceptors.response.use(
  (response) => {
    // Update CSRF token if rotated by backend (check cookies)
    // Note: The browser automatically updates the cookie, but we clear our cache
    // so the next request will fetch the fresh token from the cookie
    const stateMutatingMethods = ['post', 'put', 'delete', 'patch'];
    if (stateMutatingMethods.includes(response.config.method?.toLowerCase())) {
      // Clear cached token to force refresh on next request
      csrfToken = null;
    }
    return response;
  },
  (error) => {
    // Handle CSRF token errors
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    if (status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('csrf')) {
      // CSRF token invalid or missing - fetch new token and retry
      csrfToken = null;
      console.warn('CSRF token validation failed. Please refresh and try again.');
    }

    // Handle 402 Payment Required — License invalid/expired OR device limit (on-prem)
    if (status === 402) {
      const code = error.response?.data?.code;
      const errField = error.response?.data?.error;
      const detailObj = error.response?.data?.detail;

      if (code === 'LICENSE_INVALID') {
        // License middleware block — show full-screen lockout
        window.dispatchEvent(new CustomEvent('vivya:license-invalid', {
          detail: {
            error: error.response?.data?.error || 'License invalid',
            code,
          }
        }));
      } else if (code === 'PAYMENT_REQUIRED') {
        // Trial + grace period ended — account suspended.
        // Persist the flag so subsequent page loads block requests immediately.
        markAccountSuspended(true);
        // Dispatch once so all polling listeners (NotificationCenter, etc.) can stop.
        window.dispatchEvent(new CustomEvent('vivya:payment-required', {
          detail: {
            message: error.response?.data?.detail || 'Payment required to restore access.',
            code,
          }
        }));
      } else if (errField === 'device_limit_reached' || detailObj?.error === 'device_limit_reached') {
        // Device limit reached — dispatch distinct event for UI notification
        const msg = detailObj?.message || errField || 'Device limit reached';
        window.dispatchEvent(new CustomEvent('vivya:device-limit', {
          detail: { message: msg, maxDevices: detailObj?.max_devices, currentDevices: detailObj?.current_devices }
        }));
        console.warn('[LICENSE] Device limit reached:', msg);
      }
    }

    if (status === 403) {
      notifyForbidden(error.response?.data, error.config);
    }

    // Handle 429 Too Many Requests — rate limiting
    if (status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'];
      const waitMsg = retryAfter ? ` Please wait ${retryAfter} seconds.` : ' Please wait a moment.';
      window.dispatchEvent(new CustomEvent('vivya:rate-limited', {
        detail: { message: `Too many requests.${waitMsg}`, retryAfter }
      }));
    }

    // Handle 500/502/503/504 server errors
    if (status >= 500) {
      window.dispatchEvent(new CustomEvent('vivya:server-error', {
        detail: { status, message: error.response?.data?.detail || 'A server error occurred. Please try again.' }
      }));
    }

    // Handle network offline / no response
    if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
      window.dispatchEvent(new CustomEvent('vivya:offline', {
        detail: { message: 'Unable to connect to the server. Please check your internet connection.' }
      }));
    }

    // Handle both 401 and 403 "Not authenticated" as auth errors
    const isAuthError = status === 401 ||
                        (status === 403 && detail === 'Not authenticated');

    if (isAuthError) {
      const currentPath = window.location.pathname;

      // ── Impersonation session: restore SA token first, then redirect ──────
      const hasSaActorCookie = document.cookie
        .split(';')
        .some(c => c.trim().startsWith('sa_actor_id='));

      if (hasSaActorCookie) {
        if (!_impersonationRecoveryInProgress) {
          _impersonationRecoveryInProgress = true;
          fetch('/api/auth/impersonation-timeout-exit', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          })
            .catch(() => { /* best-effort */ })
            .finally(() => {
              _impersonationRecoveryInProgress = false;
              window.location.href = '/super-admin/dashboard';
            });
        }
        return Promise.reject(error);
      }

      // ── Skip refresh/redirect on login/public pages ───────────────────────
      const isLoginPage = currentPath === '/signin' ||
                         currentPath === '/super-admin' ||
                         currentPath === '/super-admin/' ||
                         currentPath === '/super-admin/login';

      const publicPages = [
        '/', '/pricing', '/features', '/solutions', '/resources', '/blog',
        '/case-studies', '/help', '/contact', '/partner', '/compare',
        '/privacy-policy', '/security-compliance', '/device-consent',
        '/signup', '/signup/pending', '/signup/plan', '/tryus', '/demo',
        // SSO callback must be public — it renders error states (personal_email,
        // domain_not_registered, etc.) for unauthenticated users. A forced
        // redirect to /signin here kills the error UI before it can render.
        '/sso/callback',
      ];
      const isPublicPage = publicPages.includes(currentPath) ||
                          currentPath.startsWith('/features/') ||
                          currentPath.startsWith('/resources/') ||
                          currentPath.startsWith('/tryus/') ||
                          currentPath.startsWith('/demo/') ||
                          currentPath.startsWith('/deployment-guide') ||
                          currentPath.startsWith('/onboarding/') ||
                          currentPath.startsWith('/sso/');

      if (isLoginPage || isPublicPage) {
        return Promise.reject(error);
      }

      // ── Silent token refresh ──────────────────────────────────────────────
      // Do NOT retry if this request is itself the refresh call (prevents loops).
      const isRefreshCall = error.config?.url?.includes('/api/auth/token/refresh');
      // Do NOT retry again if we already did one refresh→replay cycle for this
      // exact request and it STILL came back 401. That means the refreshed
      // session is being rejected server-side (e.g. revoked on replay-attack
      // detection, account disabled) — refreshing again would just spin the
      // same loop forever, so treat it as a persistent, unrecoverable failure.
      const alreadyRetriedAfterRefresh = !!error.config?._retriedAfter401;

      if (!isRefreshCall && !alreadyRetriedAfterRefresh) {
        // Coalesce concurrent 401s into a single refresh attempt.
        if (!_tokenRefreshPromise) {
          _tokenRefreshPromise = fetch('/api/auth/token/refresh', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          }).finally(() => {
            _tokenRefreshPromise = null;
          });
        }

        return _tokenRefreshPromise.then(async (refreshRes) => {
          if (refreshRes.ok) {
            // Token refreshed — replay the original request.
            // IMPORTANT: Strip the AbortController signal before replaying.
            // The original signal may have been aborted between the 401 and now
            // (e.g. React StrictMode cleanup, component unmount, rapid filter change).
            // A cancelled signal would cause the replay to fail instantly, which is
            // the root cause of "initial load shows no data, filter click shows data".
            const replayConfig = { ...error.config };
            delete replayConfig.signal;
            // Mark this replay so that if it ALSO 401s, we stop instead of
            // refreshing again (loop guard — see alreadyRetriedAfterRefresh).
            replayConfig._retriedAfter401 = true;
            return api.request(replayConfig);
          }
          // Refresh failed — fall through to redirect.
          throw new Error('refresh_failed');
        }).catch(() => {
          // Refresh failed or network error — clear stale auth and redirect.
          _handleUnrecoverableAuthFailure(currentPath);
          return Promise.reject(error);
        });
      }

      // Refresh call itself got a 401, OR a fresh session was rejected again
      // right after a successful refresh — clear auth and redirect.
      _handleUnrecoverableAuthFailure(currentPath);
    }
    return Promise.reject(error);
  }
);

// ============================================================
// Wrap api.get with caching + request deduplication
// ============================================================
const originalGet = api.get.bind(api);

api.get = (url, config = {}) => {
  const ttl = getCacheTTL(url);

  // Skip caching if TTL is 0 or caller explicitly opts out
  if (ttl === 0 || config._skipCache) {
    return originalGet(url, config);
  }

  const cacheKey = buildCacheKey({ method: 'get', url, params: config.params });
  const now = Date.now();

  // 1. Return cached response if fresh
  const cached = responseCache.get(cacheKey);
  if (cached && (now - cached.timestamp) < ttl) {
    return Promise.resolve(cached.data);
  }

  // 2. Return in-flight promise if same request is already pending (deduplication).
  // Skip when the caller passes an AbortSignal — signals are unique per request lifecycle.
  // Reusing the first (possibly already-aborted) promise for a second caller with a fresh
  // signal would silently deliver a CanceledError to the second caller, dropping the request.
  if (pendingRequests.has(cacheKey) && !config.signal) {
    return pendingRequests.get(cacheKey);
  }

  // 3. Make the real request
  // Wrap with Promise.resolve() to guard against async-interceptor edge cases
  // where Axios may return a non-thenable in production-minified builds.
  const requestPromise = Promise.resolve(originalGet(url, config))
    .then((response) => {
      pendingRequests.delete(cacheKey);
      // Only cache well-formed responses that expose a `.data` payload. Caching a
      // malformed/partial response (observed in production-minified async-interceptor
      // edge cases) would cause consumers reading `response.data.X` to crash.
      if (response && typeof response === 'object' && 'data' in response && response.data != null) {
        responseCache.set(cacheKey, { data: response, timestamp: Date.now() });
      }
      return response;
    })
    .catch((error) => {
      pendingRequests.delete(cacheKey);
      throw error;
    });

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Invalidate cache on mutations (POST/PUT/DELETE/PATCH)
const wrapMutationMethod = (method) => {
  const original = api[method].bind(api);
  api[method] = (...args) => {
    // Wrap with Promise.resolve() to guard against async-interceptor edge cases
    // where Axios may return a non-thenable in production-minified builds.
    // (same guard applied to api.get above — fixes "Cannot read properties of undefined (reading 'then')")
    return Promise.resolve(original(...args)).then((response) => {
      // Clear cache for the mutated URL's base path
      const url = args[0] || '';
      const basePath = url.split('?')[0].replace(/\/[^/]+\/?$/, '/');
      clearApiCache(basePath);
      return response;
    });
  };
};
['post', 'put', 'delete', 'patch'].forEach(wrapMutationMethod);

// Authentication API
export const authAPI = {
  login: async (email, password, mfaCode = null) => {
    const response = await api.post('/api/auth/login', {
      email,
      password,
      mfa_code: mfaCode
    });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/api/auth/logout');
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  setupMFA: async () => {
    const response = await api.post('/api/auth/setup-mfa');
    return response.data;
  },

  verifyMFA: async (code) => {
    const response = await api.post('/api/auth/verify-mfa', { mfa_code: code });
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  /**
   * Exchange an impersonation JTI for a real session cookie as the target user.
   * The backend replaces the auth_token cookie so subsequent /api/auth/me calls
   * return the target user with is_impersonating=true.
   */
  impersonationExchange: async (jti) => {
    const response = await api.post('/api/auth/impersonation-exchange', { jti });
    return response.data;
  },

  /**
   * Exit impersonation and restore the super-admin's own session cookie.
   * Navigate to /super-admin/dashboard after this call.
   */
  impersonationExit: async () => {
    const response = await api.post('/api/auth/impersonation-exit');
    return response.data;
  },

  /**
   * Emergency exit for an EXPIRED impersonation session.
   * The auth_token cookie is already gone — this endpoint uses only the
   * sa_actor_id cookie (kept alive 5 min beyond the session TTL) to restore
   * the SA's session and write the SESSION_ENDED audit event.
   */
  impersonationTimeoutExit: async () => {
    // Use fetch directly so the axios 401 interceptor cannot redirect us
    // before the response arrives (the endpoint returns 200 with no auth_token).
    const response = await fetch('/api/auth/impersonation-timeout-exit', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Timeout exit failed');
    }
    return response.json();
  },
};

// Employee API - removed duplicate, see comprehensive employeeAPI below

// Device Control API
export const deviceAPI = {
  remoteWipe: async (employeeId, reason, wipeType = 'selective') => {
    const response = await api.post(`/api/devices/${employeeId}/wipe`, {
      employee_id: employeeId,
      reason,
      wipe_type: wipeType,
      notify_employee: true
    });
    return response.data;
  },

  lockDevice: async (employeeId, reason, message = null) => {
    const response = await api.post(`/api/devices/${employeeId}/lock`, {
      employee_id: employeeId,
      reason,
      message,
      unlock_after_hours: null
    });
    return response.data;
  },

  unlockDevice: async (employeeId) => {
    const response = await api.post(`/api/devices/${employeeId}/unlock`);
    return response.data;
  },

  sendAlert: async (employeeId, message, priority = 'normal') => {
    const response = await api.post(`/api/devices/${employeeId}/alert`, {
      employee_id: employeeId,
      message,
      priority,
      require_acknowledgment: true
    });
    return response.data;
  },

  getDeviceStatus: async (employeeId) => {
    const response = await api.get(`/api/devices/${employeeId}/status`);
    return response.data;
  },

  getDeviceDetail: async (deviceUuid) => {
    const response = await api.get(`/api/ca/devices/${deviceUuid}/detail`);
    return response.data;
  },

  getDeviceCommands: async (params = {}) => {
    const response = await api.get('/api/devices/commands', { params });
    return response.data;
  },

  approveCommand: async (commandId) => {
    const response = await api.post(`/api/devices/commands/${commandId}/approve`);
    return response.data;
  }
};

// Monitoring API
export const monitoringAPI = {
  getDashboardMetrics: async () => {
    const response = await api.get('/api/monitoring/dashboard');
    return response.data;
  },

  getProductivityTrends: async (days = 30, department = null) => {
    const params = { days };
    if (department) params.department = department;
    
    const response = await api.get('/api/monitoring/productivity-trends', { params });
    return response.data;
  },

  getApplicationUsage: async (days = 7) => {
    const response = await api.get('/api/monitoring/application-usage', {
      params: { days }
    });
    return response.data;
  },

  getRealTimeStatus: async () => {
    const response = await api.get('/api/monitoring/real-time-status');
    return response.data;
  }
};

// AI Analytics API
export const aiAnalyticsAPI = {
  generateReport: async (employeeId, dateRange = 7, reportType = 'comprehensive') => {
    const response = await api.post('/api/ai-analytics/generate-report', {
      employee_id: employeeId,
      date_range: dateRange,
      report_type: reportType,
      include_recommendations: true
    });
    return response.data;
  },

  getDailyReport: async () => {
    const response = await api.get('/api/ai-analytics/daily-report');
    return response.data;
  },

  getHistoricalReports: async (employeeId = null, days = 30) => {
    const params = { days };
    if (employeeId) params.employee_id = employeeId;
    
    const response = await api.get('/api/ai-analytics/reports/history', { params });
    return response.data;
  },

  generateBulkAnalysis: async (employeeIds, days = 7) => {
    const response = await api.post('/api/ai-analytics/bulk-analysis', employeeIds, {
      params: { days }
    });
    return response.data;
  },

  getInsightsSummary: async (employeeId = null) => {
    const params = {};
    if (employeeId) params.employee_id = employeeId;
    
    const response = await api.get('/api/ai-analytics/insights/summary', { params });
    return response.data;
  },

  checkHealth: async () => {
    const response = await api.get('/api/ai-analytics/health');
    return response.data;
  },

  // Employee self-service AI analytics
  getMyPerformanceReport: async (days = 7) => {
    const response = await api.get('/api/ai-analytics/my-performance', {
      params: { days }
    });
    return response.data;
  },

  getMyInsightsSummary: async (days = 7) => {
    const response = await api.get('/api/ai-analytics/my-insights', {
      params: { days }
    });
    return response.data;
  },

  getProductivityIntelligence: async () => {
    const response = await api.get('/api/ai-analytics/productivity-intelligence');
    return response.data;
  },
};

// Screenshots API
export const screenshotAPI = {
  // Get screenshots with pagination and filters
  getScreenshots: async (params = {}) => {
    // Use new admin screenshots endpoint
    const response = await api.get('/api/admin/screenshots', { params });
    return response.data;
  },

  // Search screenshots (uses same endpoint with query filter)
  searchScreenshots: async (searchQuery) => {
    const response = await api.get('/api/admin/screenshots', { 
      params: { search: searchQuery } 
    });
    return response.data;
  },

  // Get screenshot details
  getScreenshotDetails: async (screenshotId) => {
    const response = await api.get(`/api/admin/screenshots/${screenshotId}`);
    return response.data;
  },

  // Get screenshot image (returns base64 encoded image data)
  getScreenshotImage: async (screenshotId) => {
    const response = await api.get(`/api/admin/screenshots/${screenshotId}`);
    return response.data;
  },

  // Delete screenshot
  deleteScreenshot: async (screenshotId) => {
    const response = await api.delete(`/api/admin/screenshots/${screenshotId}`);
    return response.data;
  },

  // Get screenshot statistics
  getStats: async () => {
    const response = await api.get('/api/admin/screenshots/stats/summary');
    return response.data;
  },

  // Manual cleanup
  cleanupOldScreenshots: async (retentionDays = 30) => {
    const response = await api.post('/api/admin/screenshots/cleanup', {
      retention_days: retentionDays
    });
    return response.data;
  },

  // Get analytics insights (backwards compatibility - now uses stats)
  getInsights: async (days = 7) => {
    const response = await api.get('/api/admin/screenshots/stats/summary');
    return response.data;
  },

  // Capture screenshot (file upload) - kept for manual uploads
  captureScreenshot: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/api/screenshots/capture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Test endpoints (for development)
  testCapture: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/api/screenshots/test/capture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  testList: async () => {
    const response = await api.get('/api/screenshots/test/list');
    return response.data;
  },

  testHealth: async () => {
    const response = await api.get('/api/screenshots/test/health');
    return response.data;
  },

  // Privacy and redaction endpoints
  getPrivacyReport: async (screenshotId) => {
    const response = await api.get(`/api/screenshots/${screenshotId}/privacy-report`);
    return response.data;
  },

  getRedactedImage: (screenshotId) => {
    return `${BACKEND_URL}/api/screenshots/${screenshotId}/redacted-image`;
  },

  applyManualRedaction: async (screenshotId, redactionSettings) => {
    const response = await api.post(`/api/screenshots/${screenshotId}/apply-redaction`, redactionSettings);
    return response.data;
  },

  getPrivacySettings: async () => {
    const response = await api.get('/api/screenshots/privacy/settings');
    return response.data;
  },

  updatePrivacySettings: async (settings) => {
    const response = await api.put('/api/screenshots/privacy/settings', settings);
    return response.data;
  },

  // Privacy Test Lab: run OCR -> detect -> redact on a sample image (in memory, nothing is stored)
  testPrivacyPipeline: async (file, { config, expect, simulate } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (config) formData.append('config', JSON.stringify(config));
    if (expect && Object.keys(expect).length) formData.append('expect', JSON.stringify(expect));
    if (simulate) formData.append('simulate', JSON.stringify(simulate));
    const response = await api.post('/api/screenshots/privacy/test', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Is OCR (Tesseract) installed and runnable? Redaction cannot work without it.
  // Custom sensitive types: grade a draft against its own examples / draft a pattern from examples
  checkSensitiveType: async (type, existingIds = []) => {
    const response = await api.post('/api/screenshots/privacy/types/check', { type, existing_ids: existingIds });
    return response.data;
  },

  suggestSensitiveTypePattern: async (samples) => {
    const response = await api.post('/api/screenshots/privacy/types/suggest', { samples });
    return response.data;
  },

  getPrivacyPipelineHealth: async () => {
    const response = await api.get('/api/screenshots/privacy/pipeline-health');
    return response.data;
  },

  // Exclusions API (separate from privacy settings)
  getExclusions: async () => {
    const response = await api.get('/api/screenshots/exclusions');
    return response.data;
  },

  updateExclusions: async (exclusions) => {
    const response = await api.put('/api/screenshots/exclusions', exclusions);
    return response.data;
  },

  // Returns unique app names and domains that tenant agents have reported
  getDiscoveredExclusions: async () => {
    const response = await api.get('/api/screenshots/exclusions/discovered');
    return response.data;
  },

  // Real-time screenshot stats
  getRealtimeStats: async () => {
    const response = await api.get('/api/screenshots/stats/realtime');
    return response.data;
  },

  // Export screenshots
  exportScreenshots: async (params = {}) => {
    const response = await api.get('/api/screenshots/export', { params });
    return response.data;
  }
};

// Employee Management API - Comprehensive
// Employee API factory.
// 'devices-employees' and 'hrms-employees' are independent RBAC modules and the
// backend derives the module from the URL prefix, so each UI section must call
// through the base path it is gated by:
//   /api/employees       → Devices & Agents → Employees  ('devices-employees')
//   /api/hrms/employees  → Attendance & HR → Employees   ('hrms-employees')
const createEmployeeAPI = (base) => ({
  // Get all employees (legacy - use getPaginated for large datasets)
  getEmployees: async (params = {}) => {
    const response = await api.get(`${base}/`, { params });
    return response.data;
  },

  // Get paginated employees - optimized for large datasets
  getEmployeesPaginated: async (params = {}) => {
    const response = await api.get(`${base}/paginated`, { params });
    return response.data;
  },

  // Get single employee
  getEmployee: async (employeeId) => {
    const response = await api.get(`${base}/${employeeId}`);
    return response.data;
  },

  // Search employees
  searchEmployees: async (params = {}) => {
    const response = await api.get(`${base}/search`, { params });
    return response.data;
  },

  // Create employee
  createEmployee: async (employeeData) => {
    const response = await api.post(base, employeeData);
    return response.data;
  },

  // Update employee
  updateEmployee: async (employeeId, employeeData) => {
    const response = await api.put(`${base}/${employeeId}`, employeeData);
    return response.data;
  },

  // Deactivate employee
  deactivateEmployee: async (employeeId) => {
    const response = await api.delete(`${base}/${employeeId}`);
    return response.data;
  },

  // Get employee activity
  getEmployeeActivity: async (employeeId, days = 7) => {
    const response = await api.get(`${base}/${employeeId}/activity`, {
      params: { days }
    });
    return response.data;
  },

  // Get employee productivity
  getEmployeeProductivity: async (employeeId, days = 30) => {
    const response = await api.get(`${base}/${employeeId}/productivity`, {
      params: { days }
    });
    return response.data;
  },

  // Get employee statistics
  getEmployeeStats: async () => {
    const response = await api.get(`${base}/stats`);
    return response.data;
  },

  // Get filter counts (department, source, license, mapping) for filter dropdowns
  getFilterCounts: async () => {
    const response = await api.get(`${base}/filter-counts`);
    return response.data;
  },

  // Get department analytics (gated by 'devices-departments' on both bases)
  getDepartmentAnalytics: async () => {
    const response = await api.get(`${base}/departments/analytics`);
    return response.data;
  },

  // Get departments for the organization (from departments collection)
  getDepartments: async (params = {}) => {
    const response = await api.get(`${base}/departments`, { params });
    return response.data;
  },

  // Create a new department
  createDepartment: async (data) => {
    const response = await api.post(`${base}/departments`, data, { _suppressAccessDenied: true });
    return response.data;
  },

  // Update a department
  updateDepartment: async (deptId, data) => {
    const response = await api.put(`${base}/departments/${deptId}`, data, { _suppressAccessDenied: true });
    return response.data;
  },

  // Delete a department
  deleteDepartment: async (deptId) => {
    const response = await api.delete(`${base}/departments/${deptId}`, { _suppressAccessDenied: true });
    return response.data;
  },

  // Export employees
  exportEmployees: async (params = {}) => {
    const response = await api.get(`${base}/export`, { params });
    return response.data;
  },

  // Get highest assigned employee code (for next-code preview in Add Employee form)
  getLastEmployeeCode: async () => {
    const response = await api.get(`${base}/last-employee-code`);
    return response.data;
  },

  // Unmap employee from their device
  unmapEmployee: async (employeeId) => {
    // _suppressAccessDenied: component handles 403 with page-specific "Employee Management" context
    const response = await api.post('/api/device-mapping/unmap-employee', { employee_id: employeeId }, { _suppressAccessDenied: true });
    return response.data;
  },

  // Map employee to a device
  mapEmployeeToDevice: async (deviceUuid, employeeId) => {
    // _suppressAccessDenied: component handles 403 with page-specific "Employee Management" context
    const response = await api.post('/api/device-mapping/map', { device_uuid: deviceUuid, employee_id: employeeId }, { _suppressAccessDenied: true });
    return response.data;
  },

  // Get unmapped devices (for mapping dropdown)
  getUnmappedDevices: async () => {
    const response = await api.get('/api/device-mapping/unmapped', { params: { include_suggestions: false } });
    return response.data;
  },

  // Bulk unmap all devices
  unmapAllDevices: async () => {
    const response = await api.post('/api/device-mapping/unmap-all');
    return response.data;
  }
});

// Devices & Agents → Employees (gated by 'devices-employees')
export const employeeAPI = createEmployeeAPI('/api/employees');

// Attendance & HR → Employees (gated by 'hrms-employees')
export const hrmsEmployeeAPI = createEmployeeAPI('/api/hrms/employees');

// Application Monitoring Real-time API
export const appMonitoringAPI = {
  // Get real-time monitoring stats
  getRealtimeStats: async () => {
    const response = await api.get('/api/application-monitoring/realtime/stats');
    return response.data;
  },

  // Get real-time employee activity
  getRealtimeEmployeeActivity: async () => {
    const response = await api.get('/api/application-monitoring/employees/realtime');
    return response.data;
  }
};

// Application Monitoring (new helpers)
export const appMonAPI = {
  getEmployees: async (search = null) => {
    const response = await api.get('/api/application-monitoring/employees', {
      params: search ? { search } : {}
    });
    return response.data;
  },
  getOverview: async (params = {}) => {
    const response = await api.get('/api/application-monitoring/overview', { params });
    return response.data;
  },
  getCategories: async (params = {}) => {
    const response = await api.get('/api/application-monitoring/categories', { params });
    return response.data;
  }
};


// Forensics API (Zone 4)
export const forensicsAPI = {
  // Create forensics session request
  createSession: async (sessionData) => {
    const response = await api.post('/api/forensics/session', sessionData);
    return response.data;
  },

  // Get all sessions with optional filters
  getSessions: async (params = {}) => {
    const response = await api.get('/api/forensics/sessions', { params });
    return response.data;
  },

  // Get single session
  getSession: async (sessionId) => {
    const response = await api.get(`/api/forensics/session/${sessionId}`);
    return response.data;
  },

  // Approve session (SECURITY_ADMIN only)
  approveSession: async (sessionId) => {
    const response = await api.post(`/api/forensics/session/${sessionId}/approve`);
    return response.data;
  },

  // Deny session (SECURITY_ADMIN only)
  denySession: async (sessionId, reason = '') => {
    const response = await api.post(`/api/forensics/session/${sessionId}/deny`, { reason });
    return response.data;
  },

  // Cancel session (SECURITY_ADMIN only)
  cancelSession: async (sessionId, reason = '') => {
    const response = await api.post(`/api/forensics/session/${sessionId}/cancel`, { reason });
    return response.data;
  },

  // Get session events
  getSessionEvents: async (sessionId, params = {}) => {
    const response = await api.get(`/api/forensics/session/${sessionId}/events`, { params });
    return response.data;
  },

  // Get pending approvals count
  getPendingCount: async () => {
    const response = await api.get('/api/forensics/sessions', { params: { approval_status: 'PENDING' } });
    return response.data?.sessions?.length || 0;
  }
};

// Health check API
export const healthAPI = {
  check: async () => {
    const response = await api.get('/api/health');
    return response.data;
  }
};

// Verdict API (EP-FM-VERDICTS-V1)
export const verdictAPI = {
  // Get verdict for a specific event
  getVerdictForEvent: async (eventId) => {
    const response = await api.get(`/api/file-monitoring/verdicts/${eventId}`);
    return response.data;
  },

  // List verdicts with filters
  listVerdicts: async (params = {}) => {
    const response = await api.get('/api/file-monitoring/verdicts', { params });
    return response.data;
  },

  // Get policy snapshot (auditor mode)
  getPolicySnapshot: async (snapshotHash) => {
    const response = await api.get(`/api/file-monitoring/policy/snapshots/${snapshotHash}`);
    return response.data;
  },

  // Get downgrade statistics
  getDowngradeStats: async () => {
    const response = await api.get('/api/file-monitoring/verdicts/stats/downgrades');
    return response.data;
  }
};

// Device Capabilities API (EP-FM-OS-CAP-TRUTH-V1)
export const deviceCapabilitiesAPI = {
  // Get enforcement stats for all devices
  getEnforcementStats: async (pathId = null) => {
    const params = pathId ? { path_id: pathId } : {};
    const response = await api.get('/api/file-monitoring/enforcement/stats', { params });
    return response.data;
  },

  // Get enforcement for a specific device
  getDeviceEnforcement: async (deviceId, requestedEnforcement = 'BLOCK') => {
    const response = await api.get(`/api/file-monitoring/enforcement/device/${deviceId}`, {
      params: { requested_enforcement: requestedEnforcement }
    });
    return response.data;
  },

  // Get device capabilities
  getDeviceCapabilities: async (deviceId) => {
    const response = await api.get(`/api/agent/capabilities/${deviceId}`);
    return response.data;
  },

  // Report device capabilities (agent endpoint)
  reportCapabilities: async (data) => {
    const response = await api.post('/api/agent/capabilities', data);
    return response.data;
  }
};

// Throttle Skip Log API
export const throttleSkipAPI = {
  // Paginated list of skip events with optional filters
  getLog: async (params = {}) => {
    const response = await api.get('/api/admin/throttle-skip-log', { params });
    return response.data;
  },

  // Aggregated stats: total, top devices/teams/operations
  getStats: async (hours = 24) => {
    const response = await api.get('/api/admin/throttle-skip-log/stats', { params: { hours } });
    return response.data;
  },
};

// ── Exported CSRF helper ──────────────────────────────────────────────────────
// Used by components that need to make raw fetch() calls (e.g. SSE streaming)
// where axios interceptors are not available.
export const getCsrfToken = async () => {
  if (csrfToken) return csrfToken;
  return fetchCSRFToken();
};

export const BACKEND_BASE_URL = BACKEND_URL;

export default api;